import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast-service';
import { SystemModuleService } from '../../../services/system-module.service';
import { Module } from '../../../services/system-domain.service';
import { GridColumn } from '../../../shared/smart-grid/smart-grid.models';

@Component({
  selector: 'app-module-management',
  templateUrl: './module-management.component.html',
  styleUrls: ['./module-management.component.scss']
})
export class ModuleManagementComponent implements OnInit {
  // Data
  modules: any = { rows: [], totalRows: 0 };
  
  // Form
  moduleForm: FormGroup;
  isEditing = false;
  
  // Modals
  private modalRef?: NgbModalRef;
  @ViewChild('moduleModal') moduleModal!: TemplateRef<any>;
  @ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;
  
  // Delete
  moduleToDelete: Module | null = null;
  
  // Grid columns
  columns: GridColumn<any>[] = [];

  constructor(
    private systemModuleService: SystemModuleService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private translateService: TranslateService,
    private modalService: NgbModal
  ) {
    this.moduleForm = this.formBuilder.group({
      moduleID: [0],
      moduleName: ['', [Validators.required, Validators.maxLength(50)]],
      moduleVersion: ['', Validators.maxLength(5)],
      moduleDescription: ['', Validators.maxLength(5000)],
      moduleTemplate: ['', Validators.maxLength(50)]
    });
  }

  ngOnInit() {
    this.buildColumns();
    this.loadModules();
  }

  get f() {
    return this.moduleForm.controls;
  }

  /**
   * Build grid columns
   */
  buildColumns() {
    this.columns = [
      {
        field: 'moduleID',
        title: this.translateService.instant('SystemAdmin.moduleID'),
        width: 80,
        sortable: true,
        filterable: true,
        filterType: 'number'
      },
      {
        field: 'moduleName',
        title: this.translateService.instant('SystemAdmin.moduleName'),
        width: 200,
        sortable: true,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'moduleVersion',
        title: this.translateService.instant('SystemAdmin.moduleVersion'),
        width: 100,
        sortable: true,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'moduleDescription',
        title: this.translateService.instant('SystemAdmin.moduleDescription'),
        width: 300,
        sortable: false,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'actions',
        title: this.translateService.instant('General.actions'),
        width: 150,
        sortable: false,
        filterable: false
      }
    ];
  }

  /**
   * Load all modules
   */
  loadModules() {
    this.systemModuleService.getAllModules().subscribe({
      next: (modules) => {
        this.modules = {
          rows: modules,
          totalRows: modules.length
        };
      },
      error: (error) => {
        console.error('Error loading modules:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingModules')]
        });
      }
    });
  }

  /**
   * Open modal to create new module
   */
  openCreateModal() {
    this.isEditing = false;
    this.moduleForm.reset({ moduleID: 0 });
    this.modalRef = this.modalService.open(this.moduleModal, { size: 'lg', backdrop: 'static' });
  }

  /**
   * Open modal to edit module
   */
  openEditModal(module: Module) {
    this.isEditing = true;
    this.moduleForm.patchValue(module);
    this.modalRef = this.modalService.open(this.moduleModal, { size: 'lg', backdrop: 'static' });
  }

  /**
   * Save module (create or update)
   */
  saveModule() {
    if (this.moduleForm.invalid) {
      Object.keys(this.moduleForm.controls).forEach(key => {
        this.moduleForm.controls[key].markAsTouched();
      });
      return;
    }

    const formValue = this.moduleForm.value;

    if (this.isEditing) {
      this.systemModuleService.updateModule(formValue.moduleID, formValue).subscribe({
        next: (result) => {
          if (result) {
            this.toastService.show({
              type: 'success',
              data: [this.translateService.instant('SystemAdmin.moduleUpdated')]
            });
            this.loadModules();
            this.closeModal();
          }
        },
        error: (error) => {
          console.error('Error updating module:', error);
          this.toastService.show({
            type: 'error',
            data: [this.translateService.instant('SystemAdmin.errorUpdatingModule')]
          });
        }
      });
    } else {
      this.systemModuleService.createModule(formValue).subscribe({
        next: (moduleId) => {
          if (moduleId) {
            this.toastService.show({
              type: 'success',
              data: [this.translateService.instant('SystemAdmin.moduleCreated')]
            });
            this.loadModules();
            this.closeModal();
          }
        },
        error: (error) => {
          console.error('Error creating module:', error);
          this.toastService.show({
            type: 'error',
            data: [this.translateService.instant('SystemAdmin.errorCreatingModule')]
          });
        }
      });
    }
  }

  /**
   * Open delete confirmation modal
   */
  openDeleteModal(module: Module) {
    this.moduleToDelete = module;
    this.modalRef = this.modalService.open(this.deleteConfirmModal, { backdrop: 'static' });
  }

  /**
   * Delete module
   */
  confirmDelete() {
    if (!this.moduleToDelete) return;

    this.systemModuleService.deleteModule(this.moduleToDelete.moduleID).subscribe({
      next: (result) => {
        if (result) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('SystemAdmin.moduleDeleted')]
          });
          this.loadModules();
          this.closeModal();
        }
      },
      error: (error) => {
        console.error('Error deleting module:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorDeletingModule')]
        });
      }
    });
  }

  /**
   * Close modal
   */
  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = undefined;
    }
    this.moduleToDelete = null;
  }
}
